import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Brain, Menu, X, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';
import { colors } from '../../styles/theme.js';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { tier } = useSubscription();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMenuOpen(false);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', isPro: false },
    { 
      path: null, 
      label: 'Training', 
      isPro: false,
      dropdown: [
        { path: '/game', label: 'N-Back Training', isPro: false },
        { path: '/speed-training', label: 'Speed Training', isPro: true }
      ]
    },
    { path: '/stats', label: 'Stats', isPro: false },
    { path: '/guides', label: 'Guides', isPro: false },
    { path: '/profile', label: 'Profile', isPro: false },
  ];

  return (
    <header style={{
      background: 'rgba(15, 23, 42, 0.95)',
      borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
      backdropFilter: 'blur(10px)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      padding: '0.75rem 2rem',
      fontFamily: '"DM Sans", system-ui, sans-serif',
      margin: 0,
      lineHeight: 'normal',
      boxSizing: 'border-box'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box'
      }}>
        {/* Logo */}
        <Link 
          to={isAuthenticated ? "/dashboard" : "/"} 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.6rem',
            textDecoration: 'none',
            margin: 0,
            padding: 0,
            lineHeight: '1'
          }}
        >
          <Brain size={24} color="#60a5fa" style={{ flexShrink: 0, display: 'block' }} />
          <span style={{
            fontSize: '1.05rem',
            fontWeight: '600',
            color: '#60a5fa',
            fontFamily: '"DM Sans", system-ui, sans-serif',
            lineHeight: '1',
            letterSpacing: 'normal',
            margin: 0,
            padding: 0,
            whiteSpace: 'nowrap'
          }}>
            IQ Reps
          </span>
        </Link>

        {/* Right Side - Menu or Sign Up Button */}
        <div style={{ position: 'relative', margin: 0, padding: 0 }}>
          {isAuthenticated ? (
            <>
              {/* Authenticated User Menu */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.875rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  background: 'transparent',
                  color: '#94a3b8',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontFamily: '"DM Sans", system-ui, sans-serif',
                  margin: 0,
                  lineHeight: '1',
                  boxSizing: 'border-box',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(100, 116, 139, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.3)';
                }}
              >
                {menuOpen ? <X size={18} style={{ flexShrink: 0, display: 'block' }} /> : <Menu size={18} style={{ flexShrink: 0, display: 'block' }} />}
                <span style={{ 
                  fontSize: '0.875rem',
                  lineHeight: '1',
                  whiteSpace: 'nowrap'
                }}>{user?.email?.split('@')[0]}</span>
              </button>

              {/* Dropdown Menu */}
              {menuOpen && (
                <>
                  <div 
                    onClick={() => setMenuOpen(false)}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 98
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 0.5rem)',
                    right: 0,
                    minWidth: '180px',
                    background: 'rgba(30, 41, 59, 0.98)',
                    border: '1px solid rgba(100, 116, 139, 0.3)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                    overflow: 'hidden',
                    zIndex: 99,
                    fontFamily: '"DM Sans", system-ui, sans-serif'
                  }}>
                    {navItems.map(item => {
                      const isActive = location.pathname === item.path;
                      
                      // Handle dropdown items
                      if (item.dropdown) {
                        return (
                          <div key={item.label}>
                            <div style={{
                              padding: '0.75rem 1rem',
                              color: colors.textTertiary,
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '1px solid rgba(100, 116, 139, 0.2)'
                            }}>
                              {item.label}
                            </div>
                            {item.dropdown.map(subItem => {
                              const subIsActive = location.pathname === subItem.path;
                              return (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  onClick={() => setMenuOpen(false)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '0.875rem 1rem 0.875rem 1.5rem',
                                    textDecoration: 'none',
                                    color: subIsActive ? '#60a5fa' : '#e2e8f0',
                                    background: subIsActive ? 'rgba(96, 165, 250, 0.1)' : 'transparent',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!subIsActive) {
                                      e.currentTarget.style.background = 'rgba(100, 116, 139, 0.2)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!subIsActive) {
                                      e.currentTarget.style.background = 'transparent';
                                    }
                                  }}
                                >
                                  <span>{subItem.label}</span>
                                  {subItem.isPro && (
                                    <Crown 
                                      size={14} 
                                      color="#fbbf24" 
                                      style={{ marginLeft: '0.5rem', flexShrink: 0 }} 
                                    />
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        );
                      }
                      
                      // Regular menu items
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMenuOpen(false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.875rem 1rem',
                            textDecoration: 'none',
                            color: isActive ? '#60a5fa' : '#e2e8f0',
                            background: isActive ? 'rgba(96, 165, 250, 0.1)' : 'transparent',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'rgba(100, 116, 139, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <span>{item.label}</span>
                          {item.isPro && (
                            <Crown 
                              size={14} 
                              color="#fbbf24" 
                              style={{ marginLeft: '0.5rem', flexShrink: 0 }} 
                            />
                          )}
                        </Link>
                      );
                    })}
                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        display: 'block',
                        padding: '0.875rem 1rem',
                        border: 'none',
                        textAlign: 'left',
                        background: 'transparent',
                        color: '#fca5a5',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontFamily: '"DM Sans", system-ui, sans-serif'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            /* Anonymous User - Sign Up Button */
            <button
              onClick={() => navigate('/signup')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1.25rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: '"DM Sans", system-ui, sans-serif',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                margin: 0,
                lineHeight: '1',
                boxSizing: 'border-box',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
              }}
            >
              Sign Up Free
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;

